import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CloudSun, Droplets, Wind, CloudRain, MapPin } from "lucide-react";

const LEVEL_DOT = { red: "bg-destructive", orange: "bg-[#CC5500]", green: "bg-[#4A7C59]" };

export default function WeatherCard() {
  const { t } = useI18n();
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [w, setW] = useState(null);

  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const q = siteId ? `?site_id=${siteId}` : "";
    setW(null);
    api.get(`/weather${q}`).then((r) => setW(r.data)).catch(() => {});
  }, [siteId]);

  if (!w) {
    return (
      <Card className="border-border" data-testid="weather-card">
        <CardContent className="p-5 text-sm text-muted-foreground">…</CardContent>
      </Card>
    );
  }
  const c = w.current || {};

  return (
    <Card className="border-border" data-testid="weather-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CloudSun className="w-4 h-4 text-primary" />
            <h3 className="font-semibold">{t("weather")}</h3>
          </div>
          {sites.length > 1 && (
            <Select value={siteId || "default"} onValueChange={(v) => setSiteId(v === "default" ? "" : v)}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="weather-site-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">{t("default_site")}</SelectItem>
                {sites.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`weather-site-${s.id}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {w.site_name && (
          <div className="text-xs text-muted-foreground flex items-center gap-1 mb-2" data-testid="weather-site-name">
            <MapPin className="w-3 h-3" />{w.site_name}
          </div>
        )}
        <div className="flex items-center gap-5 text-sm mb-4">
          <div className="text-3xl font-bold">{Math.round(c.temperature_2m ?? 0)}°C</div>
          <div className="space-y-1 text-muted-foreground text-xs">
            <div className="flex items-center gap-1"><Droplets className="w-3 h-3" />{c.relative_humidity_2m ?? "—"}% {t("humidity")}</div>
            <div className="flex items-center gap-1"><Wind className="w-3 h-3" />{c.wind_speed_10m ?? "—"} km/h</div>
            <div className="flex items-center gap-1"><CloudRain className="w-3 h-3" />{(w.daily?.precipitation_sum || [])[1] ?? 0} mm {t("tomorrow")}</div>
          </div>
        </div>
        <div className="space-y-1.5">
          {w.recommendations.map((r, i) => (
            <div key={i} className="flex items-start gap-2 text-xs" data-testid={`weather-reco-${i}`}>
              <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${LEVEL_DOT[r.level] || LEVEL_DOT.green}`} />
              <span className="text-muted-foreground">{r.message}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
